import { useState } from "react";
import { Link } from "wouter";
import { getListingImages, getClothingAttributes, conditionLabel, saleInfo } from "@shared/clothing";

type ClothingCardProps = {
  item: any;
  onOpen?: () => void;
  href?: string;
};

export function ClothingCard({ item, onOpen, href }: ClothingCardProps) {
  const images = getListingImages(item);
  const attrs = getClothingAttributes(item);
  const [active, setActive] = useState(0);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== active) setActive(idx);
  };

  const condition = conditionLabel(attrs.condition);
  const sale = saleInfo(item);

  const body = (
    <div className="group flex flex-col gap-2">
      <div className="relative aspect-square overflow-hidden rounded-xl border bg-muted">
        <span className="absolute left-2 top-2 z-10 rounded-full bg-primary/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground shadow-sm">
          1 of 1
        </span>

        {sale.onSale && (
          <span className="absolute right-2 top-2 z-10 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-foreground shadow-sm">
            Sale
          </span>
        )}

        <div
          className="flex h-full w-full snap-x snap-mandatory overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          onScroll={handleScroll}
        >
          {images.length > 0 ? (
            images.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={item.name}
                loading="lazy"
                className="h-full w-full shrink-0 snap-center object-cover"
              />
            ))
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
              No photo
            </div>
          )}
        </div>

        {images.length > 1 && (
          <div className="pointer-events-none absolute inset-x-0 bottom-2 z-10 flex justify-center gap-1">
            {images.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-1.5 rounded-full transition-colors ${
                  i === active ? "bg-white" : "bg-white/50"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-0.5">
        <p className="truncate text-sm font-medium text-foreground">{item.name}</p>
        <div className="flex items-center justify-between gap-2">
          {sale.onSale ? (
            <span className="flex items-baseline gap-1.5">
              <span className="text-sm font-bold text-primary">${sale.salePrice}</span>
              <span className="text-xs text-muted-foreground line-through">${sale.price}</span>
            </span>
          ) : (
            <span className="text-sm font-bold text-foreground">${item.price}</span>
          )}
          {attrs.size && (
            <span className="rounded-full border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              {attrs.size}
            </span>
          )}
        </div>
        {condition && (
          <span className="text-[11px] text-muted-foreground">{condition}</span>
        )}
      </div>
    </div>
  );

  if (href) {
    return (
      <Link href={href} onClick={onOpen} className="block">
        {body}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onOpen} className="block w-full text-left">
      {body}
    </button>
  );
}

export default ClothingCard;
